import { IReportEntryDay } from "../models/report";
import { getDayDoubleHours, getDayExtraHours, getDayNightHours, getDayWorkedHours } from "./util";

export interface IDayHours {
    worked_hours: number;
    extra_hours: number;
    double_hours: number;
    night_hours: number;
}

export interface IEntryTotals extends IDayHours {
    hourly_salary: number;
    percent_35: number;
    percent_15: number;
    double_salary: number;
    total_amount: number;
}

// Events flagged for destruction are still in the form state until the save
// round-trips, so they must be excluded from every calculation.
const withActiveEvents = (day: IReportEntryDay): IReportEntryDay => ({
    ...day,
    report_entry_day_events: day.report_entry_day_events.filter(e => !e._destroy),
});

export const computeDayHours = (day: IReportEntryDay): IDayHours => {
    const calc = withActiveEvents(day);
    return {
        worked_hours: getDayWorkedHours(calc),
        extra_hours: getDayExtraHours(calc),
        double_hours: getDayDoubleHours(calc),
        night_hours: getDayNightHours(calc),
    };
};

export const computeEntryTotals = (
    days: IReportEntryDay[],
    weekHours: number,
    dailySalary: number,
): IEntryTotals => {
    // Removed days also stay in the form state until the save round-trips.
    days = days.filter(d => !d._destroy);
    const worked = days.reduce((sum, d) => sum + getDayWorkedHours(withActiveEvents(d)), 0);
    const double = days.reduce((sum, d) => sum + getDayDoubleHours(withActiveEvents(d)), 0);
    const night = days.reduce((sum, d) => sum + getDayNightHours(withActiveEvents(d)), 0);
    const extra = Math.max(0, worked - weekHours - night);
    const hourly = dailySalary / 8;

    return {
        worked_hours: worked,
        extra_hours: extra,
        double_hours: double,
        night_hours: night,
        hourly_salary: hourly,
        percent_35: hourly * 1.35,
        percent_15: hourly * 1.15,
        double_salary: hourly * 2,
        total_amount: (hourly * 1.35 * extra) + (hourly * 1.15 * night) + (hourly * 2 * double),
    };
};
